import classNames from "classnames";
import currency from "currency.js";
import {
	CompanyInfoApi,
	InvoiceApi,
	InvoiceDetailsApi,
	InvoiceItemApi,
	InvoiceSummaryTaxApi,
	TaxApi,
} from "../../../../../../../services/Api/Organization/types";

const calculateTotalPrice = (amount: number | null | undefined, quantity: number, isTotalPrice?: boolean) => {
	if (!amount) return 0;

	return currency(amount).multiply(quantity).value;
};

export const calculatePriceNet = (
	amount: number | null | undefined,
	quantity: number,
	taxAmount: number,
	type?: "GROSS" | "NET"
) => {
	if (!amount) return 0;
	const money = currency(amount).multiply(quantity);

	if (type === "GROSS") {
		return money.divide(1 + taxAmount / 100).value;
	}

	return money.value;
};

export const calculatePriceGross = (
	amount: number | null | undefined,
	quantity: number,
	taxAmount: number,
	type?: "GROSS" | "NET"
) => {
	if (!amount) return 0;
	const money = currency(amount).multiply(quantity);

	if (type === "GROSS") {
		return money.value;
	}

	return money.multiply(1 + taxAmount / 100).value;
};

interface TaxWithTotalOperationObject {
	tax: TaxApi;
	total: number;
}

export const getRecalculatedSummaryTaxes = (items: InvoiceItemApi[], moneyCurrency: string, type?: "GROSS" | "NET") => {
	const totals: Record<number, TaxWithTotalOperationObject> = items
		.filter((item) => item.tax_id !== undefined)
		.reduce((acc, curr) => {
			const taxId = curr.tax_id || 0;
			const prevTotal = acc[taxId]?.total || 0;
			acc[taxId] = {
				tax: curr.tax || {},
				total: currency(prevTotal).add(calculateTotalPrice(curr.price.amount, curr.quantity)).value,
			};
			return acc;
		}, {} as Record<number, TaxWithTotalOperationObject>);

	if (Object.keys(totals).length === 0) {
		return [
			{
				tax_id: 0,
				price_net: {
					currency: moneyCurrency,
					amount: 0,
				},
				price_gross: {
					currency: moneyCurrency,
					amount: 0,
				},
				price_tax: {
					amount: 0,
					currency: moneyCurrency,
				},
			} as InvoiceSummaryTaxApi,
		];
	}

	return Object.keys(totals).map((taxId): InvoiceSummaryTaxApi => {
		const taxWithTotal: TaxWithTotalOperationObject = totals[Number(taxId)];
		const priceNetAmount = calculatePriceNet(taxWithTotal.total, 1, taxWithTotal.tax?.amount, type);
		const priceGrossAmount = calculatePriceGross(taxWithTotal.total, 1, taxWithTotal.tax?.amount, type);

		return {
			tax_id: taxWithTotal.tax?.id || 0,
			tax_name: taxWithTotal.tax?.name || "",
			tax_amount: taxWithTotal.tax.amount,
			price_net: {
				currency: "",
				amount: priceNetAmount,
			},
			price_gross: {
				currency: "",
				amount: priceGrossAmount,
			},
			price_tax: {
				amount: currency(priceGrossAmount).subtract(priceNetAmount).value,
				currency: "",
			},
		};
	});
};

export enum ExportInvoiceModalMode {
	HIDDEN,
	PRINT,
	EXPORT_TO_PDF,
}

export interface useCreateOfflineInvoiceReturn {
	createOfflineInvoiceHandler: () => Promise<void>;
	offlineCreationLoading: boolean;
	KSeFServiceUnavailable: boolean;
	displayNoOfflineCertAlert: boolean;
}

export interface useCreateOfflineInvoiceReturn {
	createOfflineInvoiceHandler: () => Promise<void>;
	offlineCreationLoading: boolean;
	KSeFServiceUnavailable: boolean;
	displayNoOfflineCertAlert: boolean;
}

export const getInvoiceDocumentTypeAbbreviation = (invoice: InvoiceApi) => {
	switch (invoice.document_type) {
		case "INVOICE":
			if (invoice.linked_invoices.some((linkedInvoice) => linkedInvoice.document_type === "ADVANCE"))
				return "ROZ";

			return "VAT";
		case "CORRECTION": {
			const hasInvoiceFromAdvance = invoice.linked_invoices.some(
				(linkedInvoice) => linkedInvoice.document_type === "INVOICE" && linkedInvoice.from_advance
			);
			const hasAdvance = invoice.linked_invoices.some(
				(linkedInvoice) => linkedInvoice.document_type === "ADVANCE"
			);

			if (hasInvoiceFromAdvance) return "KOR_ROZ";

			if (hasAdvance) return "KOR_ZAL";

			return "KOR";
		}
		case "ADVANCE":
			return "ZAL";
		default:
			return "";
	}
};

export const getInvoiceDisabledInputsClasses = (disableInputs: boolean, additionalClasses?: string) =>
	classNames(additionalClasses, { "text-muted": disableInputs });

export const getDisplayInsufficientCompanyInfo = (invoice?: InvoiceApi, companyInfo?: CompanyInfoApi) =>
	!(
		invoice &&
		companyInfo &&
		companyInfo.name &&
		companyInfo.company_name &&
		companyInfo.tax_id_no &&
		companyInfo.address_country &&
		companyInfo.address_zip_code &&
		companyInfo.address_city &&
		companyInfo.address_build_nr
	);

export const getDisplayInvoiceDetailsNotExist = (invoice?: InvoiceApi, invoiceDetails?: InvoiceDetailsApi) =>
	!(invoice && invoiceDetails && Object.keys(invoiceDetails).length > 0);
